import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";

export default function UploadTitlesPage() {
  const { token } = useContext(AuthContext);
  const [city, setCity] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const navigate = useNavigate();

  const API_BASE = "/api"; // same-origin

  const canChooseFile = Boolean(city);
  const canSubmit = Boolean(city && file && !busy);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "title");
    formData.append("city", city);

    try {
      setBusy(true);
      setStatus("");
      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus(`✅ ${data.message || "Uploaded successfully."}`);
        setFile(null);
      } else {
        setStatus(`❌ ${data.error || "Upload failed."}`);
      }
    } catch (err) {
      setStatus("❌ Network error during upload.");
    } finally {
      setBusy(false);
    }
  };

  // Download both Excel files from the server
  const handleDownloadExcel = () => {
    const excelFiles = ["NMVTIS Template.xlsx", "Clare Police book.xlsx"];

    excelFiles.forEach((filename) => {
      const link = document.createElement("a");
      link.href = `${API_BASE}/excel/${encodeURIComponent(filename)}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // Fetch recent title uploads + statuses, and poll every 5 seconds
  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const fetchUploads = async () => {
      try {
        if (!isMounted) return;
        setUploadsLoading(true);
        const res = await fetch(`${API_BASE}/uploads/titles`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          console.error("Failed to load uploads:", res.status);
          return;
        }
        const data = await res.json();
        if (isMounted) {
          setUploads(data || []);
        }
      } catch (err) {
        console.error("Error fetching uploads:", err);
      } finally {
        if (isMounted) {
          setUploadsLoading(false);
        }
      }
    };

    // Initial load
    fetchUploads();
    // Polling interval
    const intervalId = setInterval(fetchUploads, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [token, API_BASE]);

  return (
    <Container className="py-3">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white d-flex align-items-center">
              <div className="me-auto">
                <h4 className="mb-0">Upload Titles</h4>
                <small className="text-muted">
                  Choose a city, then select file(s) to upload.
                </small>
              </div>
              {/* Top-right button: Download Excel */}
              <Button
                variant="outline-primary"
                size="sm"
                className="ms-2"
                type="button"
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Card.Header>
            <Card.Body>
              {status && (
                <Alert variant={status.startsWith("✅") ? "success" : "danger"}>
                  {status}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="selectCity">
                      <Form.Label>City</Form.Label>
                      <Form.Select
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        aria-label="Select City"
                        required
                      >
                        <option value="">Select…</option>
                        <option value="Clare">Clare</option>
                        <option value="Chase">Chase</option>
                        <option value="Montcalm">Montcalm</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        This enables the file picker and upload button.
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="selectFile">
                      <Form.Label>Title File</Form.Label>
                      <Form.Control
                        type="file"
                        accept=".pdf,.docx,.txt,.mp4"
                        disabled={!canChooseFile}
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                      <Form.Text className="text-muted">
                        Accepted: PDF, DOCX, TXT, MP4
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button variant="primary" type="submit" disabled={!canSubmit}>
                    {busy ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />{" "}
                        Uploading…
                      </>
                    ) : (
                      "Upload"
                    )}
                  </Button>

                  {/* NEW: View All Titles button */}
                  <Link
                    to="/titleview"
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  >
                    View All Titles
                  </Link>

                  {/* Existing buttons */}
                  <Link
                    to="/titles/normalized"
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  >
                    Edit Titles
                  </Link>
                  <Link
                    to="/police-book"
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  >
                    Police Book
                  </Link>
                  <Link
                    to="/nmvtis"
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  >
                    NMVTIS
                  </Link>
                  <Link
                    to="/tr9"
                    className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  >
                    Generate TR-9
                  </Link>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Recent uploads + status table */}
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex align-items-center">
              <div className="me-auto">
                <h5 className="mb-0">Recent Title Uploads</h5>
                <small className="text-muted">
                  Status is updated automatically as the worker processes files.
                </small>
              </div>
              {uploadsLoading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Card.Header>
            <Card.Body>
              {uploads.length === 0 && !uploadsLoading ? (
                <div className="text-muted small">
                  No recent title uploads found.
                </div>
              ) : (
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Location</th>
                      <th>Uploaded</th>
                      <th>Status</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((u) => (
                      <tr key={u.id}>
                        <td>{u.filename}</td>
                        <td>{u.location || "-"}</td>
                        <td>
                          {u.created_at
                            ? new Date(u.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          <UploadStatusBadge status={u.status} />
                        </td>
                        <td>{u.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

function UploadStatusBadge({ status }) {
  const map = {
    uploaded: { label: "Queued", variant: "secondary" },
    processing: { label: "Processing", variant: "info" },
    success: { label: "Success", variant: "success" },
    duplicate: { label: "Duplicate", variant: "warning" },
    error: { label: "Error", variant: "danger" },
  };

  const info = map[status] || {
    label: status || "Unknown",
    variant: "secondary",
  };

  return <Badge bg={info.variant}>{info.label}</Badge>;
}

